import type { ReactNode, Ref } from "react";

import { sanitizeFormLabel } from "~/util/sanitizeHtml";

import ErrorMessage from "./ErrorMessage";

interface InputWrapperProps {
  actionLabel?: ReactNode;
  children: ReactNode;
  description?: string | ReactNode;
  descriptionPosition?: "top" | "bottom";
  disabled?: boolean;
  error?: string;
  hideLabel?: boolean;
  id: string;
  label: string;
  ref?: Ref<HTMLDivElement>;
  required?: boolean;
}

/**
 * Wrapper component for form inputs that adds a label and error message.
 *
 * @param {ReactNode} actionLabel - Add additional action to sit next to the label.
 * @param {ReactNode} children - The children of the component (required).
 * @param {string} description - The description of the input.
 * @param {boolean} disabled - If the input is disabled or not.
 * @param {string} error - The error message to display.
 * @param {boolean} hideLabel - If the label should be hidden (can still be read by screen readers).
 * @param {string} label - The label of the input (required).
 * @param {string} id - The id of the input (required).
 * @param {boolean} required - If the input is required or not.
 */
function InputWrapper({
  actionLabel,
  children,
  description,
  descriptionPosition = "top",
  disabled,
  error,
  hideLabel = false,
  id,
  label,
  ref,
  required,
}: InputWrapperProps) {
  let baseClasses = "w-full relative";

  if (disabled) baseClasses += " opacity-50";

  return (
    <div className={baseClasses} ref={ref}>
      <div className={`flex justify-between${hideLabel ? "sr-only" : ""}`}>
        <label
          htmlFor={id}
          className="block text-sm leading-6 font-medium text-gray-900"
        >
          <span
            dangerouslySetInnerHTML={
              typeof label === "string"
                ? { __html: sanitizeFormLabel(label) }
                : undefined
            }
          />
          {required ? (
            <>
              <span className="not-sr-only text-red-500">*</span>{" "}
              <span className="sr-only">(Required)</span>
            </>
          ) : (
            ""
          )}
        </label>
        {actionLabel && actionLabel}
      </div>
      {description && descriptionPosition === "top" && (
        <p id={`${id}-description`} className="text-sm leading-6 text-gray-500">
          {description}
        </p>
      )}
      {children}
      {description && descriptionPosition === "bottom" && (
        <p id={`${id}-description`} className="text-sm leading-6 text-gray-500">
          {description}
        </p>
      )}
      {error && <ErrorMessage id={`${id}-error`}>{error}</ErrorMessage>}
    </div>
  );
}

export default InputWrapper;
